<template>
    <label
        class="text-sm font-semibold text-secondary-600 block mb-1"
    >
        <span :id="labelId"><slot name="label" /></span>
        <el-form-item :prop="prop" v-if="withFormItem">
            <el-select
                :disabled="isDisabled"
                :multiple="isMultiple"
                :collapse-tags="collapseTags"
                ref="select"
                class="w-full"
                v-model="valueProxy"
                :clearable="clearable"
                :filterable="filterable"
                :allow-create="allowCreate"
                :maxlength="maxlength"
                :placeholder="placeholder"
                :loading="loading"
                :remote="remote"
                :remote-method="remoteMethod" 
                :reserve-keyword="reserveKeyword"
                :size="size"
                :valueKey="valueKey"
                :popper-class="popperClass"
                @focus="handleFocus"
                @blur="handleBlur"
                @visible-change="handleDropdownVisible"
                @change="onChange"
            >
                <template v-if="group">
                  <el-option-group
                    v-for="optionGroup in normalizedOptions"
                    :key="optionGroup.label"
                    :label="optionGroup.label"
                  >
                    <el-option
                      v-for="option in optionGroup.options"
                      :key="option.id"
                      :label="option.name"
                      :value="option.value"
                      :disabled="option.disabled"
                      @select="onSelect"
                    />
                  </el-option-group>
                </template>
                <template v-else>
                    <el-option 
                        v-for="option in normalizedOptions"
                        :key="option.id"
                        :label="option.name"
                        :value="option.value"
                        :disabled="option.disabled"
                        @select="onSelect"
                    />
                </template>
            </el-select>
        </el-form-item>
        <el-select v-else
            :disabled="isDisabled"
            :multiple="isMultiple"
            :collapse-tags="collapseTags"
            ref="select"
            class="w-full"
            v-model="valueProxy"
            :clearable="clearable"
            :filterable="filterable"
            :allow-create="allowCreate"
            :maxlength="maxlength"
            :placeholder="placeholder"
            :loading="loading"
            :remote="remote"
            :remote-method="remoteMethod" 
            :reserve-keyword="reserveKeyword"
            :size="size"
            :valueKey="valueKey"
            :popper-class="popperClass"
            @focus="handleFocus"
            @blur="handleBlur"
            @visible-change="handleDropdownVisible"
            @change="onChange"
        >
            <template v-if="group">
                <el-option-group
                    v-for="optionGroup in normalizedOptions"
                    :key="optionGroup.label"
                    :label="optionGroup.label"
                >
                    <el-option
                        v-for="option in optionGroup.options"
                        :key="option.id"
                        :label="option.name"
                        :value="option.value"
                        :disabled="option.disabled"
                        @select="onSelect"
                    />
                </el-option-group>
            </template>
            <template v-else>
                <el-option
                    v-for="option in normalizedOptions"
                    :key="option.id"
                    :label="option.name"
                    :value="option.value"
                    :disabled="option.disabled"
                    @select="onSelect"
                />
            </template>
        </el-select>
    </label>
</template>

<script>
import { dropdownState } from '@/state/dropdownState'

export default {
  name: 'AccessibleSelect',
  props: {
    value: [String, Number, Object, null],
    prop: { 
      type: String,
      required: true 
    },
    options: { // [{ id, value, name }] or [{ label, options: [{ id, value, name }] }] when group=true
      type: Array, 
      required: true 
    },
    group: {
      type: Boolean,
      default: false
    }, 
    idPrefix: { 
      type: String, 
      default: 'select' 
    },
    withFormItem: {
      type: Boolean,
      default: true,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    isMultiple: {
      type: Boolean,
      default: false,
    },
    collapseTags: {
      type: Boolean,
      default: false,
    },
    allowCreate: {
      type: Boolean,
      default: false,
    },
    maxlength: {
      type: String,
      default: null,
    },
    placeholder: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    remote: {
      type: Boolean,
      default: false,
    },
    remoteMethod: {
      type: Function,
      default: null,
    },
    reserveKeyword: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: null,
    },
    valueKey: {
      type: String,
      default: null,
    },
    popperClass: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      // Para selects remotos necesitamos que filterable esté activo desde el inicio
      filterable: this.remote,
      observer: null,
      tagsObserver: null,
      pasteListener: null,
      unwatchInputHovering: null,
      inputTabListener: null
    }
  },
  mounted () {
    this.pasteListener = this.onInputPaste.bind(this)
    this.clearKeydownListener = this.onClearKeydown.bind(this)
    this.tagCloseKeydownListener = this.onTagCloseKeydown.bind(this)
    this.focusinListener = this.onSelectFocusin.bind(this)
    this.focusoutListener = this.onSelectFocusout.bind(this)
    this.inputTabListener = this.onInputTab.bind(this)
    this.applyComboboxAria()
    this.attachPasteListener()
    this.attachFocusListeners()
    this.forceClearIconPersistent()
    this.watchInputHovering()
    this.enhanceClearButton()
    this.enhanceTagCloseButtons()
    this.attachInputTabListeners()
    this.attachTagsObserver()
    this.syncInputTitle()
  },
  computed: {
    labelId () {
      return `${this.idPrefix}-${this.prop}-label`
    },
    valueProxy: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    normalizedOptions () {
      if (this.group) {
        // Para opciones agrupadas: [{ label, options: [{ id, value, name }] }]
        return this.options?.map(optionGroup => ({
          ...optionGroup,
          options: optionGroup.options.map(option => ({
            ...option,
            value: option.value !== undefined ? option.value : option.id
          }))
        }))
      } else {
        // Para opciones simples: [{ id, value, name }]
        return this.options?.map(option => ({
          ...option,
          value: option.value !== undefined ? option.value : option.id
        }))
      }
    }
  },
  watch: {
    value () {
      // Al cambiar la selección (incluso de forma externa) se re-renderizan los tags
      // y el icono de borrar todo; hay que reaplicar la accesibilidad por teclado.
      this.$nextTick(() => {
        this.forceClearIconPersistent()
        this.enhanceClearButton()
        if (this.isMultiple) this.enhanceTagCloseButtons()
        this.syncInputTitle()
      })
    },
    normalizedOptions () {
      // Element UI mide el ancho del popper al abrirlo. En selects remotos las
      // opciones llegan después de esa medición y el popper mantiene el ancho
      // "vacío/cargando". Forzamos updatePopper cuando las opciones cambian y el
      // desplegable está abierto para que se re-mida y quede consistente.
      this.$nextTick(() => {
        const select = this.$refs.select
        if (select && select.visible && typeof select.broadcast === 'function') {
          select.broadcast('ElSelectDropdown', 'updatePopper')
        }
      })
    }
  },
  methods: {
    getSelectRoot () {
      return this.$refs.select?.$el || null
    },
    getSelectInputs () {
      const root = this.getSelectRoot()
      if (!root) return []

      // Element UI select uses different inputs:
      // - .el-input__inner: readonly display input (single mode)
      // - .el-select__input: real editable filter input (filterable / remote / multiple)
      const filterInput = root.querySelector('input.el-select__input')
      const displayInput = root.querySelector('input.el-input__inner')

      return [filterInput, displayInput].filter(Boolean)
    },
    getComboboxInput () {
      const root = this.getSelectRoot()
      if (!root) return null
      return root.querySelector('input.el-select__input') || root.querySelector('input.el-input__inner')
    },
    applyComboboxAria () {
      const input = this.getComboboxInput()
      if (!input) return
      input.setAttribute('role', 'combobox')
      input.setAttribute('aria-haspopup', 'listbox')
      input.setAttribute('aria-expanded', 'false')
      // Asociar explícitamente todos los inputs al label mediante aria-labelledby.
      // En modo múltiple, el input activo es input.el-select__input (dentro de .el-select__tags),
      // una estructura tan profunda que NVDA no asciende hasta el <label> envolvente.
      // La asociación explícita garantiza compatibilidad WCAG 2.1 AA con NVDA + Chrome.
      const allInputs = this.getSelectInputs()
      allInputs.forEach(inp => {
        inp.setAttribute('aria-labelledby', this.labelId)
      })
    },
    syncInputTitle () {
      // WCAG 2.1 AA 1.4.13: cuando la opción seleccionada es larga y el input la
      // recorta con ellipsis, exponemos el texto completo como title para que
      // el usuario vidente lo vea en hover. El lector de pantalla ya lo obtiene
      // del value/aria-label del input.
      const displayInput = this.getSelectRoot()?.querySelector('input.el-input__inner')
      if (!displayInput) return
      const fullText = displayInput.value || ''
      if (fullText) {
        displayInput.setAttribute('title', fullText)
      } else {
        displayInput.removeAttribute('title')
      }
    },
    attachPasteListener () {
      if (!this.remote || !this.remoteMethod) return
      const inputs = this.getSelectInputs()
      inputs.forEach(input => {
        if (!input || input.__accessiblePasteListenerAdded) return
        input.addEventListener('paste', this.pasteListener)
        input.__accessiblePasteListenerAdded = true
      })
    },
    attachFocusListeners () {
      const root = this.getSelectRoot()
      if (!root || root.__accessibleFocusListenersAdded) return
      root.addEventListener('focusin', this.focusinListener)
      root.addEventListener('focusout', this.focusoutListener)
      root.__accessibleFocusListenersAdded = true
    },
    onSelectFocusin () {
      // Forzar inputHovering para que Element UI muestre el icono de limpiar con teclado
      this.forceClearIconPersistent()
      this.$nextTick(() => {
        this.enhanceClearButton()
        this.enhanceTagCloseButtons()
      })
    },
    onSelectFocusout (e) {
      const root = this.getSelectRoot()
      // Solo desactivar si el foco sale completamente del select y NO hay valor
      // seleccionado. Si hay valor, mantenemos inputHovering=true para que el icono
      // de "borrar todo" permanezca en el DOM y sea alcanzable con Shift+Tab desde
      // el siguiente campo (WCAG 2.1 AA - 2.1.1 Teclado).
      if (root && !root.contains(e.relatedTarget)) {
        if (!this.hasValue()) {
          const select = this.$refs.select
          if (select) {
            select.inputHovering = false
          }
        }
      }
    },
    hasValue () {
      const v = this.value
      if (this.isMultiple) return Array.isArray(v) && v.length > 0
      return v !== undefined && v !== null && v !== ''
    },
    forceClearIconPersistent () {
      // Element UI renderiza el icono de "borrar todo" con v-if="inputHovering && hasValue".
      // Para que sea un punto de tabulación estable (alcanzable con Tab y Shift+Tab)
      // mantenemos inputHovering=true siempre que exista un valor seleccionado.
      const select = this.$refs.select
      if (!select) return
      if (this.hasValue()) {
        select.inputHovering = true
      }
    },
    watchInputHovering () {
      // Element UI vuelve a poner inputHovering=false en mouseleave del select.
      // Vigilamos la propiedad reactiva del componente hijo y la restauramos
      // mientras haya valor, para que el icono de "borrar todo" no desaparezca
      // del DOM y siga siendo alcanzable por teclado (WCAG 2.1 AA - 2.1.1).
      const select = this.$refs.select
      if (!select || this.unwatchInputHovering) return
      this.unwatchInputHovering = select.$watch('inputHovering', val => {
        if (val === false && this.hasValue()) {
          this.$nextTick(() => {
            select.inputHovering = true
          })
        }
      })
    },
    enhanceClearButton () {
      const root = this.getSelectRoot()
      if (!root) return
      const clearIcon = root.querySelector('.el-icon-circle-close')
      if (!clearIcon) return
      // Reaplicamos siempre los atributos accesibles (Element UI puede recrear el
      // elemento). Solo el listener se guarda para no duplicarse.
      clearIcon.setAttribute('tabindex', '0')
      clearIcon.setAttribute('role', 'button')
      clearIcon.setAttribute('aria-label', this.placeholder ? `Borrar ${this.placeholder}` : 'Borrar selección')
      if (!clearIcon.__accessibleClearListenerAdded) {
        clearIcon.addEventListener('keydown', this.clearKeydownListener)
        clearIcon.__accessibleClearListenerAdded = true
      }
    },
    onClearKeydown (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        const select = this.$refs.select
        if (select && typeof select.handleClearClick === 'function') {
          select.handleClearClick(e)
        }
        // Devolver foco al input principal
        this.$nextTick(() => {
          const input = this.getComboboxInput()
          if (input) input.focus()
        })
      }
    },
    enhanceTagCloseButtons () {
      // Modo múltiple: cada tag seleccionado tiene un icono de cierre (.el-tag__close)
      // que Element UI solo maneja con ratón. Lo hacemos accesible por teclado.
      const root = this.getSelectRoot()
      if (!root) return
      const closeIcons = root.querySelectorAll('.el-tag__close')
      closeIcons.forEach(icon => {
        // Reaplicamos siempre los atributos: Element UI puede reescribir el DOM.
        const tag = icon.closest('.el-tag')
        const textEl = tag ? tag.querySelector('.el-select__tags-text') : null
        const label = textEl ? textEl.innerText.trim() : ''
        icon.setAttribute('tabindex', '0')
        icon.setAttribute('role', 'button')
        icon.setAttribute('aria-label', label ? `Quitar ${label}` : 'Quitar elemento')
        if (!icon.__accessibleTagCloseListenerAdded) {
          icon.addEventListener('keydown', this.tagCloseKeydownListener)
          icon.__accessibleTagCloseListenerAdded = true
        }
      })
    },
    attachTagsObserver () {
      // Element UI regenera los tags y el icono de "borrar todo" (al enfocar, al
      // cambiar inputHovering o al recalcular el ancho), perdiendo el tabindex/rol
      // que aplicamos. Observamos los cambios para volver a hacerlos accesibles.
      const root = this.getSelectRoot()
      if (!root || this.tagsObserver) return
      this.tagsObserver = new MutationObserver(() => {
        this.enhanceClearButton()
        this.enhanceTagCloseButtons()
      })
      this.tagsObserver.observe(root, { childList: true, subtree: true })
    },
    onTagCloseKeydown (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        e.stopPropagation()
        // El icono ya tiene el manejador de clic de Element UI; lo disparamos.
        e.currentTarget.click()
        // Tras eliminar el tag, devolvemos el foco al input principal del select.
        this.$nextTick(() => {
          const input = this.getComboboxInput()
          if (input) input.focus()
        })
      }
    },
    attachInputTabListeners () {
      // Element UI intercepta Tab en el input del select con handleTab() y llama
      // event.stopPropagation(). En modo múltiple+filterable, además, .el-input__inner
      // tiene tabindex="-1" impuesto por Element UI. Esto provoca que Tab hacia delante
      // desde el input pueda saltar el icono de "borrar todo". Interceptamos Tab en
      // capture y redirigimos manualmente el foco al clear-all cuando existe.
      const inputs = this.getSelectInputs()
      inputs.forEach(input => {
        if (!input || input.__accessibleTabListenerAdded) return
        input.addEventListener('keydown', this.inputTabListener, true)
        input.__accessibleTabListenerAdded = true
      })
    },
    onInputTab (e) {
      // Solo Tab hacia delante (no Shift+Tab: para atrás el orden natural ya funciona)
      if (e.key !== 'Tab' || e.shiftKey) return
      const root = this.getSelectRoot()
      if (!root) return
      const clearIcon = root.querySelector('.el-icon-circle-close')
      if (!clearIcon) return
      // El clear-all existe pero el navegador podría saltarlo por la manipulación
      // interna de Element UI. Redirigimos el foco manualmente.
      e.preventDefault()
      e.stopPropagation()
      clearIcon.focus()
    },
    onChange (item) {
      // console.log('change', item)
      this.$emit('change', item)
      // Tras seleccionar un valor, Element UI crea el icono de limpiar (v-if) y, en
      // modo múltiple, nuevos tags. Necesitamos enhancerlos para el teclado.
      this.$nextTick(() => {
        this.forceClearIconPersistent()
        this.enhanceClearButton()
        this.enhanceTagCloseButtons()
        this.attachInputTabListeners()
        this.syncInputTitle()
      })
    },
    onSelect (item) {
      // console.log('select', item)
      this.$emit('select', item)
    },
    handleFocus () {
      this.filterable = true
      this.$nextTick(() => {
        this.applyComboboxAria()
        this.attachPasteListener()
        this.attachInputTabListeners()
        this.enhanceClearButton()
      })
    },
    handleBlur () {
      if (!this.$refs.select || !this.$refs.select.visible) {
        this.filterable = false
      }
    },
    onInputPaste (e) {
      if (!this.remoteMethod) return

      const pastedText = (e.clipboardData || window.clipboardData).getData('text')
      const target = e.target
      const isReadonlyTarget = !!(target && target.readOnly)

      // If the user pasted into the readonly display input, Element UI won't insert the text.
      // In that case, prevent default, open the dropdown and inject the text into the filter input.
      if (isReadonlyTarget) {
        e.preventDefault()
        this.filterable = true

        // Try to open dropdown so the filter input exists.
        const select = this.$refs.select
        if (select && typeof select.toggleMenu === 'function') {
          select.toggleMenu()
        }

        this.$nextTick(() => {
          const root = this.getSelectRoot()
          const filterInput = root?.querySelector('input.el-select__input')
          if (filterInput) {
            filterInput.value = pastedText
            // Trigger Element UI internal input handler
            filterInput.dispatchEvent(new Event('input', { bubbles: true }))
            filterInput.dispatchEvent(new Event('change', { bubbles: true }))
          }
          this.remoteMethod(pastedText)
        })
        return
      }

      // Normal case: allow default paste, then read actual input value.
      this.$nextTick(() => {
        const query = target && typeof target.value === 'string' ? target.value : pastedText
        this.remoteMethod(query)
      })
    },
    handleDropdownVisible (visible) {
      this.$emit('visible-change', visible)
      // Actualiza el estado global para que los Dialogs puedan bloquear close-on-click-modal
      if (visible) {
        dropdownState.isAnyDropdownOpen = true
      } else {
        setTimeout(() => {
          dropdownState.isAnyDropdownOpen = false
        }, 200)
      }
      const input = this.getComboboxInput()

      if (!visible) {
        if (input) {
          input.setAttribute('aria-expanded', 'false')
          input.removeAttribute('aria-activedescendant')
        }
        // En selects remotos mantenemos filterable activo para no romper la búsqueda
        if (!this.remote) {
          this.filterable = false
        }
        if (this.observer) {
          this.observer.disconnect()
          this.observer = null
        }
        return
      }

      this.filterable = true

      this.$nextTick(() => {
        this.applyComboboxAria()
        this.attachPasteListener()

        const dropdowns = Array.from(document.querySelectorAll('.el-select-dropdown'))
        const visibleDropdown = dropdowns.reverse().find(dd =>
          dd.style.display !== 'none' && dd.querySelector('.el-select-dropdown__list')
        )
        if (!visibleDropdown) return

        const list = visibleDropdown.querySelector('.el-select-dropdown__list')
        if (!list) return

        list.setAttribute('role', 'listbox')

        // Handle group headers and options
        const groupHeaders = list.querySelectorAll('.el-select-group__title')
        groupHeaders.forEach((header, index) => {
          header.setAttribute('role', 'group')
          header.setAttribute('id', `${this.prop}-group-${index}`)
        })

        const items = list.querySelectorAll('.el-select-dropdown__item')
        items.forEach((item, index) => {
          item.setAttribute('role', 'option')
          item.setAttribute('id', `${this.prop}-option-${index}`)
          const text = item.innerText.trim()
          const isSelected = text === this.getLabelByValue(this.valueProxy)
          item.setAttribute('aria-selected', isSelected.toString())
        })

        const input = this.getComboboxInput()
        if (!input) return

        input.setAttribute('aria-expanded', 'true')

        const selectedIndex = this.findOptionIndex(this.valueProxy)
        if (selectedIndex >= 0) {
          input.setAttribute('aria-activedescendant', `${this.prop}-option-${selectedIndex}`)
        }

        this.observer = new MutationObserver(() => {
          const active = list.querySelector('.el-select-dropdown__item.hover')
          if (active) {
            input.setAttribute('aria-activedescendant', active.id)
          }
        })
        this.observer.observe(list, {
          subtree: true,
          attributes: true,
          attributeFilter: ['class']
        })
      })
    },
    getLabelByValue (value) {
      if (this.group) {
        for (const optionGroup of this.normalizedOptions) {
          const opt = optionGroup.options.find(o => o.value === value)
          if (opt) return opt.name
        }
        return ''
      } else {
        const opt = this.normalizedOptions.find(o => o.value === value)
        return opt ? opt.name : ''
      }
    },
    findOptionIndex (value) {
      if (this.group) {
        let index = 0
        for (const optionGroup of this.normalizedOptions) {
          for (const option of optionGroup.options) {
            if (option.value === value) {
              return index
            }
            index++
          }
        }
        return -1
      } else {
        return this.normalizedOptions.findIndex(opt => opt.value === value)
      }
    }
  },
  destroyed () {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
    if (this.tagsObserver) {
      this.tagsObserver.disconnect()
      this.tagsObserver = null
    }
    if (this.unwatchInputHovering) {
      this.unwatchInputHovering()
      this.unwatchInputHovering = null
    }

    const inputs = this.getSelectInputs ? this.getSelectInputs() : []
    inputs.forEach(input => {
      if (input && this.pasteListener) {
        input.removeEventListener('paste', this.pasteListener)
      }
    })

    const root = this.getSelectRoot ? this.getSelectRoot() : null
    if (root) {
      root.removeEventListener('focusin', this.focusinListener)
      root.removeEventListener('focusout', this.focusoutListener)
      const clearIcon = root.querySelector('.el-icon-circle-close')
      if (clearIcon) {
        clearIcon.removeEventListener('keydown', this.clearKeydownListener)
      }
      const closeIcons = root.querySelectorAll('.el-tag__close')
      closeIcons.forEach(icon => {
        if (this.tagCloseKeydownListener) {
          icon.removeEventListener('keydown', this.tagCloseKeydownListener)
        }
      })
      // Limpieza de los listeners de Tab en los inputs
      const allInputs = this.getSelectInputs ? this.getSelectInputs() : []
      allInputs.forEach(input => {
        if (input && this.inputTabListener) {
          input.removeEventListener('keydown', this.inputTabListener, true)
        }
      })
    }
  }
}
</script>

<style scoped>
::v-deep .el-select:focus-within .el-input__suffix .el-icon-circle-close {
  display: inline-block !important;
}
::v-deep .el-input__suffix-inner .el-icon-circle-close {
  color: var(--secondary-400) !important; 
  padding-left: 0.25rem;
  padding-right: 0.25rem;
}
::v-deep .el-input__suffix-inner .el-icon-circle-close:hover {
  color: var(--secondary-800) !important;
}
::v-deep .el-input__suffix-inner .el-icon-circle-close:focus {
  color: var(--secondary-800) !important; 
  outline: 2px solid var(--secondary-800) !important;
}
::v-deep .el-tag__close:focus {
  color: #fff !important;
  background-color: var(--secondary-600) !important;
  outline: 2px solid var(--secondary-800) !important;
  outline-offset: 1px;
}
::v-deep .el-tag__close:focus-visible {
  outline: 2px solid var(--secondary-800) !important;
  outline-offset: 1px;
}
</style>
